import { useRef, useState } from "react";
import { Camera, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const MAX_FILE_BYTES = 4.5 * 1024 * 1024; // matches backend 6MB base64 cap (~4.5MB raw)

/**
 * Optional photo attachment for public submissions. Converts a chosen image
 * to a base64 data URL and calls `onChange({ photo_data, photo_caption })`.
 * Pass `null` to `value` to clear the field from the parent.
 */
export function SubmissionPhotoField({ value, onChange, label = "Photo (optional)" }) {
  const inputRef = useRef(null);
  const [caption, setCaption] = useState(value?.photo_caption || "");
  const [preview, setPreview] = useState(value?.photo_data || null);

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPG, PNG, WebP).");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      toast.error("Image too large — please keep it under 4.5 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setPreview(dataUrl);
      onChange({ photo_data: dataUrl, photo_caption: caption });
    };
    reader.readAsDataURL(file);
  };

  const clear = () => {
    setPreview(null);
    setCaption("");
    onChange(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const updateCaption = (val) => {
    setCaption(val);
    if (preview) onChange({ photo_data: preview, photo_caption: val });
  };

  return (
    <div className="p-4 bg-white/5 rounded-lg border border-white/5 space-y-3" data-testid="submission-photo-field">
      <p className="text-[11px] text-amber-500 uppercase tracking-[0.2em] font-semibold">
        {label}
      </p>
      <p className="text-xs text-white/50 -mt-1">
        Attach one photo of the location. It will appear on the site after an editor approves your submission.
      </p>

      {!preview ? (
        <div className="flex flex-col gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
            data-testid="submission-photo-input"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            className="border-white/10 text-white hover:bg-white/5 justify-start"
            data-testid="submission-photo-pick-btn"
          >
            <Camera className="w-4 h-4 mr-2" />
            Choose a photo…
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="relative">
            <img
              src={preview}
              alt="preview"
              className="w-full max-h-64 object-contain rounded-md border border-white/10 bg-black/50"
              data-testid="submission-photo-preview"
            />
            <button
              type="button"
              onClick={clear}
              className="absolute top-2 right-2 bg-black/70 hover:bg-red-500 text-white rounded-full p-1.5 transition-colors"
              aria-label="Remove photo"
              data-testid="submission-photo-clear-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div>
            <label htmlFor="photo-caption" className="block text-xs text-white/60 mb-1">
              Caption (optional)
            </label>
            <Input
              id="photo-caption"
              value={caption}
              onChange={(e) => updateCaption(e.target.value)}
              placeholder="e.g. Southeast corner, taken March 2025"
              className="bg-white/5 border-white/10 text-white text-sm"
              data-testid="submission-photo-caption"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default SubmissionPhotoField;
